<template>
	<div>
		<div
			v-if="open && isInitializing"
			class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
		>
			<div class="rounded-lg bg-white p-8 shadow-lg">
				<h2 class="mb-4 text-xl font-semibold">Port a Number</h2>
				<div class="flex items-center">
					<span class="fa-spin fa-spinner mr-3 text-2xl text-blue-500"></span>
					<span>Loading wizard...</span>
				</div>
			</div>
		</div>

		<StepModal
			title="Port a Number"
			description="Complete this form to request a number port"
			:open="open"
			:disable-overlay-close="true"
			@update:open="$emit('update:open', $event)"
			@complete="handleComplete"
			@step-change="onStepChange"
		>
			<div>
				<Step v-if="showTenantStep" title="Select Tenant" :is-required="true" :is-valid="isTenantStepValid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<FormField label="Tenant" has-icon icon-class="fa-chevron-down">
								<select v-model="porting.selectedTenant" aria-label="Selected Tenant">
									<option value="">Select a tenant</option>
									<option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
										{{ tenant.companyName }}
									</option>
								</select>
							</FormField>
						</div>
					</template>
				</Step>
				<Step title="Number Details" :is-required="true" :is-valid="isStep1Valid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<FormField label="Main billing number to port">
								<input
									v-model="porting.number"
									placeholder="Enter telephone number (e.g. 01202000111)"
								/>
							</FormField>
							<FormField label="Account Number with Provider">
								<input
									v-model="porting.account_number"
									placeholder="Your account number with current provider"
								/>
							</FormField>
							<div style="margin-top: 16px; display: flex; align-items: center; gap: 10px">
								<div class="flex items-center gap-2">
									<input id="loa-checkbox" v-model="loaConfirmed" type="checkbox" class="tw-toggle" />
									<label
										for="loa-checkbox"
										style="cursor: pointer; user-select: none; font-size: 15px"
									>
										I have a Letter of Authority from the end user regarding this port request
									</label>
								</div>
							</div>
							<div v-if="loaConfirmed && requiresLOA" class="mt-4">
								<p class="mb-2 text-sm text-gray-700">
									Please upload the Letter of Authority (LOA) document for this port request.
								</p>
								<form enctype="multipart/form-data">
									<input
										id="loaFileInput"
										ref="fileInput"
										name="fileInput"
										type="file"
										accept="*"
										@change="loaFileChanged"
									/>
								</form>
							</div>
						</div>
					</template>
				</Step>
				<Step title="Lines & Installation Address" :is-required="true" :is-valid="isStep2Valid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<div class="grid grid-cols-1 gap-2 md:grid-cols-2">
								<FormField label="Number of Lines">
									<input
										v-model.number="porting.lines"
										type="number"
										min="1"
										placeholder="Number of lines"
										class="w-full"
									/>
								</FormField>
								<FormField label="Number of Channels">
									<input
										v-model.number="porting.channels"
										type="number"
										min="1"
										placeholder="Number of channels"
										class="w-full"
									/>
								</FormField>
							</div>
							<div v-if="porting.lines == 1" class="mb-2 flex items-center gap-2">
								<input
									id="multi-line-checkbox"
									v-model="porting.multi_line"
									type="checkbox"
									class="tw-toggle"
								/>
								<label
									for="multi-line-checkbox"
									style="cursor: pointer; user-select: none; font-size: 15px"
								>
									I know this is a multi-line (e.g. FeatureLine / Skype Number)
								</label>
							</div>
							<FormField label="Title">
								<select v-model="porting.salutation">
									<option value="Mr">Mr</option>
									<option value="Mrs">Mrs</option>
									<option value="Miss">Miss</option>
									<option value="Ms">Ms</option>
									<option value="Dr">Dr</option>
								</select>
							</FormField>
							<div class="grid grid-cols-1 gap-2 md:grid-cols-2">
								<FormField label="First name">
									<input v-model="porting.first_name" placeholder="First name" />
								</FormField>
								<FormField label="Last name">
									<input v-model="porting.last_name" placeholder="Last name" />
								</FormField>
							</div>
							<FormField label="Company name (Optional)">
								<input v-model="porting.company_name" placeholder="Company name" />
							</FormField>
							<FormField label="Property name / number">
								<input v-model="porting.property_number" placeholder="Property name / number" />
							</FormField>
							<FormField label="Street">
								<input v-model="porting.street" placeholder="Street" />
							</FormField>
							<FormField label="Town/city">
								<input v-model="porting.town" placeholder="Town/city" />
							</FormField>
							<FormField label="Postcode">
								<input v-model="porting.postcode" placeholder="Postcode" />
							</FormField>
						</div>
					</template>
				</Step>
				<Step title="Number Configuration" :is-required="true" :is-valid="isStep3Valid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<div class="mb-2 text-sm text-gray-700">
								Main Billing Number: <span class="font-semibold">{{ porting.number }}</span>
							</div>

							<div v-if="lookupLoading" class="mb-4 text-center">
								<span class="fa-spin fa-spinner mr-2"></span> Looking up number information...
							</div>

							<div v-if="rhNotFound" class="mb-4 rounded-md bg-gray-50 p-4 dark:bg-offmaindark">
								<p class="mb-3">
									We can't find the Rangeholder for this number. Please select both the Rangeholder
									and your current provider (LCP):
								</p>
								<SearchableDropdown
									v-model="porting.rh"
									label="Rangeholder"
									:options="lcpOptions"
									placeholder="Select the Rangeholder"
									:multi-select="false"
								/>
								<div class="mt-4">
									<SearchableDropdown
										v-model="porting.currentProvider"
										label="Current Provider (LCP)"
										:options="lcpOptions"
										placeholder="Select your current provider"
										:multi-select="false"
									/>
									<div v-if="porting.currentProvider === 'Other'" class="mt-2">
										<FormField label="Specify Provider">
											<input
												v-model="porting.otherProvider"
												placeholder="Enter your current provider"
												class="w-full"
											/>
										</FormField>
									</div>
								</div>
							</div>
							<div v-else-if="porting.rh" class="mb-4 rounded-md bg-gray-50 p-4 dark:bg-offmaindark">
								<p class="mb-3">
									The Rangeholder is <strong>{{ porting.rh }}</strong
									>. Is this also your current provider (LCP)?
								</p>
								<div class="mb-3 flex space-x-4">
									<label class="flex cursor-pointer items-center">
										<input
											type="radio"
											name="lcpSameAsRh"
											:checked="lcpIsSameAsRh === true"
											class="mr-2"
											@change="handleLcpSelection(true)"
										/>
										<span>Yes</span>
									</label>
									<label class="flex cursor-pointer items-center">
										<input
											type="radio"
											name="lcpSameAsRh"
											:checked="lcpIsSameAsRh === false"
											class="mr-2"
											@change="handleLcpSelection(false)"
										/>
										<span>No</span>
									</label>
								</div>
								<div v-if="lcpIsSameAsRh === false" class="mt-3">
									<SearchableDropdown
										v-model="porting.currentProvider"
										label="Current Provider (LCP)"
										:options="lcpOptions"
										placeholder="Select your current provider"
										:multi-select="false"
									/>
									<div v-if="porting.currentProvider === 'Other'" class="mt-2">
										<FormField label="Specify Provider">
											<input
												v-model="porting.otherProvider"
												placeholder="Enter your current provider"
												class="w-full"
											/>
										</FormField>
									</div>
								</div>
							</div>

							<div
								v-else-if="porting.associated_numbers.length >= 100"
								class="mb-2 rounded bg-red-100 px-4 py-2 text-sm text-red-700"
							>
								You can only add up to 100 numbers.
							</div>
							<p class="text-sm text-gray-500">Add additional, associated numbers to port</p>
							<div class="flex w-full flex-col gap-2 md:flex-row md:items-end">
								<FormField label="Single Number" class="w-full">
									<input
										v-model="singleNumberInput"
										placeholder="Single Number e.g. 01171234000"
										class="w-full"
									/>
								</FormField>
								<FormField label="Upper Range (optional)" class="w-full">
									<input
										v-model="upperRangeInput"
										placeholder="Upper Range e.g. 01171234009"
										class="w-full"
									/>
								</FormField>
								<button
									class="jmiBtn green mt-1 h-10 md:mt-0"
									:disabled="!canAddNumberRange"
									@click="addNumberRange"
								>
									<span class="fa-light fa-plus"></span>
								</button>
							</div>
							<p class="mt-1 text-xs text-gray-500">
								To add a range, enter both fields. To add one number, just use Single Number.
							</p>
							<div v-if="porting.associated_numbers.length">
								<div class="mb-2 flex justify-end">
									<button class="jmiBtn red" @click="porting.associated_numbers = []">
										Remove All
									</button>
								</div>
								<div style="max-height: 300px; overflow-y: auto">
									<table class="mt-4 min-w-full">
										<thead>
											<tr>
												<th class="border-r px-2 py-1 text-left">Number</th>
												<th
													class="cursor-pointer border-r px-2 py-1 text-center"
													@click="setAllAssociatedNumbersAction('port')"
												>
													Port
												</th>
												<th
													class="cursor-pointer border-r px-2 py-1 text-center"
													@click="setAllAssociatedNumbersAction('keep')"
												>
													Keep
												</th>
												<th
													class="cursor-pointer border-r px-2 py-1 text-center"
													@click="setAllAssociatedNumbersAction('drop')"
												>
													Drop
												</th>
												<th class="px-2 py-1 text-center"></th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="(num, idx) in porting.associated_numbers" :key="num.number">
												<td class="border-r px-2 py-1">{{ num.number.padStart(11, '0') }}</td>
												<td class="border-r px-2 py-1 text-center">
													<input
														v-model="num.action"
														type="radio"
														:name="'action-' + num.number"
														value="port"
													/>
												</td>
												<td class="border-r px-2 py-1 text-center">
													<input
														v-model="num.action"
														type="radio"
														:name="'action-' + num.number"
														value="keep"
													/>
												</td>
												<td class="border-r px-2 py-1 text-center">
													<input
														v-model="num.action"
														type="radio"
														:name="'action-' + num.number"
														value="drop"
													/>
												</td>
												<td class="px-2 py-1 text-center">
													<button class="jmiBtn red" @click="removeNumber(idx)">
														Remove
													</button>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
							<p v-if="porting.associated_numbers.length >= 100" class="mt-2 text-xs text-red-500">
								Maximum 100 numbers allowed.
							</p>
						</div>
					</template>
				</Step>
				<Step title="Comments & Porting Date" :is-required="true" :is-valid="isStep4Valid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<FormField label="Additional Comments">
								<textarea
									v-model="porting.comments"
									placeholder="Any additional information about this port"
									rows="3"
								></textarea>
							</FormField>
							<FormField label="Requested Date">
								<div class="flex flex-col gap-2 md:flex-row md:items-center">
									<div class="flex items-center gap-2 p-2">
										<span style="font-size: 15px">ASAP</span>
										<input
											id="asap-toggle"
											v-model="asapToggle"
											type="checkbox"
											class="tw-toggle"
										/>
									</div>
									<div v-if="!asapToggle">
										<input
											v-model="porting.requested_date"
											type="date"
											:placeholder="'Select date'"
											:style="'width: 250px'"
											:min="minPortDate.toISOString().slice(0, 10)"
										/>
									</div>
								</div>
								<div class="mt-1 text-xs text-blue-600">
									<span v-if="asapToggle">
										<span v-if="rhNotFound && !porting.rh">
											Estimated time to complete is unknown
										</span>
										<span v-else>
											ASAP selected (estimated {{ calculatedWorkingDays }} working days{{
												rhNotFound ? ' - unverified' : ''
											}})
										</span>
									</span>
									<span v-else>
										<span v-if="rhNotFound && !porting.rh">
											Estimated time to complete is unknown
										</span>
										<span v-else>
											{{
												portingDaysMessage ||
												`Minimum porting time: ${calculatedWorkingDays} working days${rhNotFound ? ' - unverified' : ''}`
											}}
										</span>
									</span>
								</div>
							</FormField>
						</div>
					</template>
				</Step>
				<Step title="Application Config" :is-required="true" :is-valid="isStep5Valid">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<FormField>
								<div class="flex items-center gap-2">
									<input
										id="configure-later"
										v-model="porting.configureLater"
										type="checkbox"
										class="tw-toggle"
									/>
									<label for="configure-later" style="margin: 0">Configure later</label>
								</div>
							</FormField>
							<div v-if="!porting.configureLater">
								<div class="mb-4">
									<AccountSelect v-model="porting.accountId" :accounts="accounts" />
								</div>
								<div
									class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm dark:border-offmaindark dark:bg-offmaindark"
								>
									<h3 class="mb-3 text-base font-medium text-gray-700 dark:text-offwhite">
										Configuration
									</h3>
									<ApplicationConfig
										v-model="porting"
										:account="selectedAccount"
										:tenants="tenants"
									/>
								</div>
							</div>
							<p class="mt-2 text-xs text-gray-500">
								<span class="text-red-500">*</span> Required fields
							</p>
						</div>
					</template>
				</Step>
				<Step title="Summary" :is-required="false">
					<template #default="{ isActive }">
						<div v-if="isActive" class="max-h-[70vh] space-y-4 overflow-y-auto">
							<h3 class="text-lg font-medium dark:text-offwhite">Review Porting Request</h3>
							<div class="space-y-2 rounded-md bg-gray-100 p-4 dark:bg-offmaindark dark:text-offwhite">
								<div>
									<p class="font-medium dark:text-offwhite">Number to Port:</p>
									<p class="dark:text-offwhite">{{ porting.number }}</p>
								</div>
								<div>
									<p class="font-medium dark:text-offwhite">Account Number:</p>
									<p class="dark:text-offwhite">{{ porting.account_number }}</p>
								</div>
								<div>
									<p class="font-medium dark:text-offwhite">Lines / Channels:</p>
									<p class="dark:text-offwhite">
										{{ porting.lines }} / {{ porting.channels
										}}{{ porting.multi_line ? ' (Multi-line/FeatureLine)' : '' }}
									</p>
								</div>
								<div>
									<p class="font-medium dark:text-offwhite">Installation Address:</p>
									<p class="dark:text-offwhite">
										{{ porting.salutation }} {{ porting.first_name }} {{ porting.last_name }}
									</p>
									<p v-if="porting.company_name" class="dark:text-offwhite">
										{{ porting.company_name }}
									</p>
									<p class="dark:text-offwhite">{{ porting.property_number }}</p>
									<p class="dark:text-offwhite">{{ porting.street }}</p>
									<p class="dark:text-offwhite">{{ porting.town }}</p>
									<p class="dark:text-offwhite">{{ porting.postcode }}</p>
								</div>
								<div v-if="porting.associated_numbers && porting.associated_numbers.length">
									<p class="font-medium dark:text-offwhite">Associated Numbers:</p>
									<ul>
										<li
											v-for="num in porting.associated_numbers"
											:key="num.number"
											class="dark:text-offwhite"
										>
											{{ num.number }} ({{ num.action }})
										</li>
									</ul>
								</div>
								<div v-if="porting.comments && porting.comments.trim()">
									<p class="font-medium dark:text-offwhite">Comments:</p>
									<p class="dark:text-offwhite">{{ porting.comments }}</p>
								</div>
								<div>
									<p class="font-medium dark:text-offwhite">Requested Date:</p>
									<p class="dark:text-offwhite">
										{{
											porting.requested_date_display === 'ASAP'
												? 'As soon as possible'
												: porting.requested_date
													? $global.dateFormat(new Date(porting.requested_date))
													: 'Not set'
										}}
									</p>
								</div>
								<div>
									<p class="font-medium dark:text-offwhite">Destination:</p>
									<p class="dark:text-offwhite">
										Tenant: {{ getTenantName(porting.selectedTenant) }}
									</p>
									<p class="dark:text-offwhite">
										Application: {{ $global.prettyPrint(porting.app) || 'None' }}
									</p>
								</div>
							</div>
							<div
								class="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-600 dark:bg-yellow-900"
							>
								<p class="text-sm text-yellow-800 dark:text-yellow-200">
									By completing this request, you are authorising
									{{
										resellerName
											? resellerName + ' to port the specified number'
											: 'the specified number to be ported'
									}}. This process will take a minimum of
									{{ calculatedWorkingDays ? calculatedWorkingDays : 'several' }} working days to
									complete.
								</p>
							</div>
						</div>
					</template>
				</Step>
			</div>
		</StepModal>
	</div>
</template>

<script>
import accountsApi from '../../scripts/accounts/accounts.api'
import numberportsApi from '../../scripts/numberports/numberports.api.js'
import AccountSelect from './AccountSelect.vue'
import ApplicationConfig from './ApplicationConfig.vue'
import FormField from './FormField.vue'
import { Step, StepModal } from '../ui/steps'
import { lcpOptions } from '../../config/lcpOptions'
import SearchableDropdown from './SearchableDropdown.vue'
import translateNumber from '../../scripts/utils/translateNumber.js'

export default {
	components: { Step, StepModal, AccountSelect, ApplicationConfig, FormField, SearchableDropdown },
	props: {
		open: Boolean,
		userRoles: Array,
		accountsProp: Array,
		tenants: Array,
		initialData: Object
	},
	emits: ['update:open', 'complete'],
	data() {
		return {
			allAccounts: [],
			associatedNumbersText: '',
			singleNumberInput: '',
			upperRangeInput: '',
			asapToggle: true,
			porting: {
				number: '',
				currentProvider: '',
				otherProvider: '',
				account_number: '',
				salutation: 'Mr',
				first_name: '',
				last_name: '',
				company_name: '',
				property_number: '',
				street: '',
				town: '',
				postcode: '',
				authorisedPerson: '',
				businessName: '',
				pin: '',
				authorised: false,
				selectedTenant: '',
				app: '',
				lcp: '',
				rh: '',
				lines: 1,
				channels: 1,
				multi_line: false,
				comments: '',
				associated_numbers: [],
				accountId: '',
				configureLater: false,
				requested_date: '',
				requested_date_display: 'ASAP',
				appConfig: {}
			},
			currentStep: 0,
			stepsCount: 7,
			loaConfirmed: false,
			lookupLoading: false,
			lookupMinDays: null,
			lcpIsSameAsRh: null,
			rhNotFound: false,
			calculatedWorkingDays: 10,
			isInitializing: false,
			loaFile: null
		}
	},
	computed: {
		accounts() {
			if (!this.porting.selectedTenant) {
				return this.allAccounts
			}

			const selectedTenantId = parseInt(this.porting.selectedTenant)

			const filtered = this.allAccounts.filter((account) => {
				const accountTenantId =
					account.tenantId ||
					(account.tenant && account.tenant.id) ||
					(typeof account.tenant === 'number' ? account.tenant : null) ||
					account.tenant_id

				if (accountTenantId !== undefined && accountTenantId !== null) {
					return accountTenantId.toString() === selectedTenantId.toString()
				}

				return false
			})

			return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
		},
		showTenantStep() {
			return this.tenants && (this.tenants.length > 1 || this.$global.hasUserRole(this.userRoles, 'reseller'))
		},
		isTenantStepValid() {
			return !!this.porting.selectedTenant
		},
		selectedTenantDetails() {
			if (!this.porting.selectedTenant || !this.tenants) return null

			const tenantId = parseInt(this.porting.selectedTenant)
			const tenant = this.tenants.find((t) => t.id === tenantId)

			return tenant || null
		},
		resellerName() {
			if (
				this.selectedTenantDetails &&
				this.selectedTenantDetails.reseller &&
				this.selectedTenantDetails.reseller.name
			) {
				return this.selectedTenantDetails.reseller.name
			}
			return null
		},
		isStep1Valid() {
			return (
				this.porting.number.trim() !== '' &&
				this.porting.account_number.trim() !== '' &&
				this.loaConfirmed &&
				(!this.requiresLOA || !!this.loaFile)
			)
		},
		isStep2Valid() {
			return (
				this.porting.lines >= 1 &&
				this.porting.channels >= 1 &&
				this.porting.first_name.trim() !== '' &&
				this.porting.last_name.trim() !== '' &&
				this.porting.property_number.trim() !== '' &&
				this.porting.street.trim() !== '' &&
				this.porting.town.trim() !== '' &&
				this.porting.postcode.trim() !== ''
			)
		},
		isStep3Valid() {
			if (this.rhNotFound) {
				if (!this.porting.rh) return false

				if (this.porting.currentProvider === 'Other') {
					return !!this.porting.otherProvider
				}
				return !!this.porting.currentProvider
			}

			if (this.porting.rh) {
				if (this.lcpIsSameAsRh === null) return false
				if (this.lcpIsSameAsRh === true) return true

				if (this.porting.currentProvider === 'Other') {
					return !!this.porting.otherProvider
				}
				return !!this.porting.currentProvider
			}

			return false
		},
		isStep4Valid() {
			if (!this.asapToggle && !this.porting.requested_date) {
				return false
			}
			return true
		},
		isStep5Valid() {
			if (this.porting.configureLater) return true
			return this.porting.selectedTenant !== '' && this.porting.accountId && this.porting.app !== ''
		},
		selectedAccount() {
			return this.accounts.find((a) => a.id === this.porting.accountId)
		},
		isStepDisabled() {
			if (this.currentStep === 0) return !this.isTenantStepValid
			if (this.currentStep === 1) return !this.isStep1Valid
			if (this.currentStep === 2) return !this.isStep2Valid
			if (this.currentStep === 3) return !this.isStep3Valid
			if (this.currentStep === 4) return !this.isStep4Valid
			if (this.currentStep === 5) return !this.isStep5Valid
			return false
		},
		canAddNumberRange() {
			if (this.porting.associated_numbers.length >= 100) return false
			let start = this.singleNumberInput.trim()
			let end = this.upperRangeInput.trim()
			let isValidNumber = (num) => /^\d{10,11}$/.test(num)
			if (!start) return false
			if (end) {
				if (!isValidNumber(start) || !isValidNumber(end)) return false
				if (isNaN(Number(start)) || isNaN(Number(end))) return false
				if (Number(start) > Number(end)) return false
				return true
			} else {
				if (!isValidNumber(start)) return false
				if (isNaN(Number(start))) return false
				return true
			}
		},
		minPortDate() {
			let lcp = (this.porting.currentProvider || '').trim()
			let rh = (this.porting.rh || '').trim()
			let lines = Number(this.porting.lines) || 1
			let multi = !!this.porting.multi_line
			let days = 0

			if (lcp && rh && lcp === rh && lines >= 1 && lines <= 5) {
				days = 5
			} else if (lcp && rh && lcp !== rh && lines === 1) {
				days = 10
			} else {
				days = 10
			}

			if (multi) {
				days = days * 2
			} else if (lines > 1) {
				days = days * 2
			}

			let d = new Date()
			d.setDate(d.getDate() + 1)

			let added = 0
			while (added < days) {
				let wd = d.getDay()
				if (wd !== 0 && wd !== 6) added++
				if (added < days) {
					d.setDate(d.getDate() + 1)
				}
			}
			d.setHours(9, 0, 0, 0) // Set default time to 9:00 AM
			return d
		},
		portingDaysMessage() {
			if (this.lookupMinDays) {
				return `Minimum porting time: ${this.lookupMinDays} working days`
			}
			return ''
		},
		workingDays() {
			let lcp = (this.porting.currentProvider || '').trim()
			let rh = (this.porting.rh || '').trim()
			let lines = Number(this.porting.lines) || 1
			let multi = !!this.porting.multi_line
			let days = 0

			if (lcp && rh && lcp === rh && lines >= 1 && lines <= 5) {
				days = 5
			} else if (lcp && rh && lcp !== rh && lines === 1) {
				days = 10
			} else {
				days = 10
			}

			if (multi || lines > 1) {
				days = days * 2
			}

			return days
		},
		formatRequestedDateTime(datetimeStr) {
			try {
				const date = new Date(datetimeStr)
				const formattedDate = this.$global.dateFormat(date)

				let hours = date.getHours()
				const minutes = date.getMinutes().toString().padStart(2, '0')
				const ampm = hours >= 12 ? 'PM' : 'AM'
				hours = hours % 12
				hours = hours ? hours : 12
				const formattedTime = `${hours}:${minutes} ${ampm}`

				return `${formattedDate} at ${formattedTime}`
			} catch (e) {
				console.error('Error formatting datetime:', e)
				return datetimeStr
			}
		},
		requiresLOA() {
			const loaPref = this.tenants.find((t) => t.id === this.porting.selectedTenant)?.reseller
				?.portingLOAPreference

			console.log('LOA Preference:', loaPref)

			if (!loaPref) return false

			if (loaPref === 'always') return true

			if (loaPref === 'never') return false

			if (loaPref === 'nongeo') {
				const e164mbn = translateNumber(this.porting.number, 'E164')

				return (
					e164mbn.startsWith('443') ||
					e164mbn.startsWith('444') ||
					e164mbn.startsWith('445') ||
					e164mbn.startsWith('447') ||
					e164mbn.startsWith('448') ||
					e164mbn.startsWith('449')
				)
			}

			return false
		}
	},
	watch: {
		open(val) {
			if (val) {
				this.reset()
				this.loadAccounts()

				if (this.initialData) {
					this.$nextTick(() => {
						this.populateInitialData()
					})
				}
			}
		},
		'porting.selectedTenant'(newVal) {
			this.porting.accountId = ''
			this.porting.app = ''

			if (newVal && this.tenants) {
				const tenantId = parseInt(newVal)
				const tenantObj = this.tenants.find((t) => t.id === tenantId)
				if (tenantObj) {
					this.porting.tenant = tenantObj
				}
			} else {
				this.porting.tenant = null
			}

			if (newVal) {
				this.loadAccounts()
			}
		},
		asapToggle(val) {
			if (val) {
				this.porting.requested_date = this.minPortDate.toISOString().slice(0, 10)
				this.porting.requested_date_display = 'ASAP'
			} else if (this.porting.requested_date_display === 'ASAP') {
				this.porting.requested_date = ''
				this.porting.requested_date_display = ''
			}
		},
		workingDays(newValue) {
			this.calculatedWorkingDays = newValue
		},
		'porting.currentProvider'(newVal) {
			if (newVal === 'Other' && !this.porting.otherProvider) {
				this.porting.lcp = ''
			} else if (newVal && this.rhNotFound && this.lcpIsSameAsRh === false) {
				if (!newVal.includes('(Unverified)')) {
					this.porting.lcp = newVal + ' (Unverified)'
				}

				this.$nextTick(() => {
					this.calculatedWorkingDays = this.workingDays
					this.$forceUpdate()
				})
			}
		},
		'porting.otherProvider'(newVal) {
			if (this.porting.currentProvider === 'Other' && newVal) {
				if (!newVal.includes('(Unverified)')) {
					this.porting.lcp = newVal + ' (Unverified)'
				}

				this.$nextTick(() => {
					this.calculatedWorkingDays = this.workingDays
				})
			}
		},
		'porting.rh': function (newVal) {
			if (newVal && this.rhNotFound) {
				if (!newVal.includes('(Unverified)')) {
					this.porting.rh = newVal + ' (Unverified)'
				}
				this.$nextTick(() => {
					this.calculatedWorkingDays = this.workingDays
				})
			}
		}
	},
	created() {
		this.lcpOptions = [...lcpOptions, 'Other']

		if (this.open) {
			this.reset()
			this.loadAccounts()

			if (this.initialData) {
				this.$nextTick(() => {
					this.populateInitialData()
				})
			}
		}
	},
	methods: {
		loaFileChanged(event) {
			if (!event?.target?.files?.length) {
				this.loaFile = null
				return
			}
			this.loaFile = event.target.files[0]
		},
		populateInitialData() {
			if (!this.initialData) return

			this.porting.number = this.initialData.number || ''
			this.porting.account_number = this.initialData.account_number || ''
			this.porting.salutation = this.initialData.salutation || 'Mr'
			this.porting.first_name = this.initialData.first_name || ''
			this.porting.last_name = this.initialData.last_name || ''
			this.porting.company_name = this.initialData.company_name || ''
			this.porting.property_number = this.initialData.property_number || ''
			this.porting.street = this.initialData.street || ''
			this.porting.town = this.initialData.town || ''
			this.porting.postcode = this.initialData.postcode || ''
			this.porting.lines = this.initialData.lines || 1
			this.porting.channels = this.initialData.channels || 1
			this.porting.multi_line = this.initialData.multi_line || false
			this.porting.comments = this.initialData.comments || ''

			this.porting.rh = this.initialData.rh || ''
			this.porting.lcp = this.initialData.lcp || ''

			if (this.porting.rh && this.porting.lcp) {
				this.lcpIsSameAsRh = this.porting.rh === this.porting.lcp
			}

			if (this.initialData.associated_numbers && this.initialData.associated_numbers.length > 0) {
				this.porting.associated_numbers = [...this.initialData.associated_numbers]
			}

			this.porting.selectedTenant = this.initialData.selectedTenant || ''
			if (this.initialData.tenant) {
				this.porting.tenant = this.initialData.tenant
			}

			this.porting.accountId = this.initialData.accountId || ''
			if (this.initialData.account) {
				this.porting.account = this.initialData.account
			}

			this.porting.app = this.initialData.app || ''

			if (this.initialData.appConfig) {
				this.porting.appConfig = JSON.parse(JSON.stringify(this.initialData.appConfig))
			}

			this.loaConfirmed = false
		},
		reset() {
			this.loaConfirmed = false
			this.associatedNumbersText = ''
			this.singleNumberInput = ''
			this.upperRangeInput = ''
			this.lcpIsSameAsRh = null
			this.rhNotFound = false
			this.currentStep = 0
			this.asapToggle = true
			this.lookupLoading = false
			this.lookupMinDays = null

			this.porting = {
				number: '',
				currentProvider: '',
				otherProvider: '',
				account_number: '',
				salutation: 'Mr',
				first_name: '',
				last_name: '',
				company_name: '',
				property_number: '',
				street: '',
				town: '',
				postcode: '',
				authorisedPerson: '',
				businessName: '',
				pin: '',
				authorised: false,
				selectedTenant: '',
				app: '',
				lcp: '',
				rh: '',
				lines: 1,
				channels: 1,
				multi_line: false,
				comments: '',
				associated_numbers: [],
				accountId: '',
				configureLater: false,
				requested_date: '',
				requested_date_display: 'ASAP',
				appConfig: {}
			}

			if (this.asapToggle) {
				this.$nextTick(() => {
					const minDate = this.minPortDate.toISOString().slice(0, 10)
					this.porting.requested_date = minDate
				})
			}

			if (this.tenants && this.tenants.length === 1) {
				this.porting.selectedTenant = this.tenants[0].id
				this.porting.tenant = this.tenants[0]
			} else if (
				this.$global &&
				this.$global.hasUserRole &&
				this.userRoles &&
				this.$global.hasUserRole(this.userRoles, 'reseller')
			) {
				const selectedTenant = this.$global.getSelectedTenant && this.$global.getSelectedTenant()
				this.porting.selectedTenant = selectedTenant || ''

				if (selectedTenant && this.tenants) {
					const tenantId = parseInt(selectedTenant)
					const tenantObj = this.tenants.find((t) => t.id === tenantId)
					if (tenantObj) {
						this.porting.tenant = tenantObj
					}
				}
			} else {
				this.porting.selectedTenant = ''
				this.porting.tenant = null
			}

			this.calculatedWorkingDays = 10
		},
		async loadAccounts() {
			try {
				const result = await accountsApi.getAll({ limit: 2500 })

				if (!result.data || !result.data.results) {
					console.error('Unexpected API response format:', result)
					this.allAccounts = []
					return
				}

				this.allAccounts = result.data.results

				if (this.allAccounts.length > 0 && this.allAccounts[0].name) {
					this.allAccounts = this.allAccounts.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
				}
			} catch (error) {
				console.error('Error loading accounts:', error)
				this.allAccounts = []
			}
		},
		updateAssociatedNumbers() {
			this.porting.associated_numbers = this.associatedNumbersText
				.split('\n')
				.map((line) => line.trim())
				.filter((line) => line !== '')
		},
		getTenantName(tenantId) {
			if (!tenantId) return 'Not selected'
			const tenant = this.tenants?.find((t) => t.id === parseInt(tenantId))
			return tenant ? tenant.companyName : 'Unknown'
		},
		async handleComplete() {
			const payload = { ...this.porting }
			payload.mbn = this.porting.number
			if (payload.currentProvider === 'Other' && payload.otherProvider) {
				payload.currentProvider = payload.otherProvider
				payload.lcp = payload.otherProvider
			} else if (payload.currentProvider) {
				payload.lcp = payload.lcp || payload.currentProvider
			}
			if (this.rhNotFound && payload.rh && !payload.rh.includes('(Unverified)')) {
				payload.rh = payload.rh + ' (Unverified)'
			}
			payload.associated_numbers = this.porting.associated_numbers.map((n) => ({
				number: n.number.padStart(11, '0'),
				action: n.action
			}))
			if (this.porting.selectedTenant) {
				if (this.porting.tenant) {
					payload.tenant = this.porting.tenant
				} else {
					const tenantId = parseInt(this.porting.selectedTenant)
					const tenantObj = this.tenants.find((t) => t.id === tenantId)
					payload.tenant = tenantObj || { id: this.porting.selectedTenant }
				}
			}

			if (this.porting.requested_date && !this.asapToggle) {
				try {
					const dateStr = this.porting.requested_date
					const date = new Date(dateStr)

					if (!isNaN(date.getTime())) {
						date.setHours(9, 0, 0, 0)

						const year = date.getFullYear()
						const month = String(date.getMonth() + 1).padStart(2, '0')
						const day = String(date.getDate()).padStart(2, '0')
						payload.requested_date = `${year}-${month}-${day}`

						const formattedDate = this.$global.dateFormat
							? this.$global.dateFormat(date)
							: new Intl.DateTimeFormat('en-GB', {
									day: '2-digit',
									month: 'short',
									year: 'numeric'
								}).format(date)

						payload.requested_date_display = formattedDate
					} else {
						payload.requested_date = this.porting.requested_date
					}
				} catch (e) {
					console.error('Error formatting date:', e)
					payload.requested_date = this.porting.requested_date
				}
			}

			if (this.asapToggle) {
				payload.requested_date_display = 'ASAP'
			}

			const requestedConfig = {}

			if (this.porting.accountId) {
				requestedConfig.accountId = this.porting.accountId
			}

			if (this.porting.app) {
				requestedConfig.application_name = this.porting.app

				if (this.porting.appConfig && Object.keys(this.porting.appConfig).length > 0) {
					const appConfig = this.porting.appConfig[this.porting.app] || {}
					requestedConfig.application_config = appConfig
				} else {
					requestedConfig.application_config = {}
				}
			}

			if (Object.keys(requestedConfig).length > 0) {
				payload.requested_config = requestedConfig
			}

			let result
			try {
				if (this.loaFile)
					payload.loa_file = {
						pending: true
					}

				result = await numberportsApi.create(payload)

				if (result?.identifiers[0]?.id && this.loaFile) {
					const formData = new FormData()
					formData.append('file', this.loaFile)
					await numberportsApi.uploadLoa(result.identifiers[0].id, formData)
				}
			} catch (e) {
				console.error('Error submitting porting request:', e)
				alert('An error occurred while submitting the porting request.')
				return
			}
			if (result && result.success === false) {
				alert(result.error || 'Porting request failed.')
				return
			}
			this.$emit('complete', payload)
			this.reset()
		},
		async lookupRangeholder() {
			this.lookupLoading = true
			this.rhNotFound = false
			try {
				const result = await numberportsApi.numberLookup(this.porting.number)
				if (result && result.data) {
					if (result.data.success === false) {
						this.rhNotFound = true
						this.porting.rh = this.porting.rh || ''
						this.porting.lcp = ''
						this.lcpIsSameAsRh = null
					} else if (result.data.data?.rh) {
						this.porting.rh = result.data.data.rh
						this.porting.lcp = result.data.data.lcp || ''
						this.lcpIsSameAsRh = null
					} else {
						this.rhNotFound = true
						this.porting.rh = this.porting.rh || ''
						this.porting.lcp = ''
					}
				}
			} catch (e) {
				this.rhNotFound = true
				this.porting.rh = this.porting.rh || ''
				this.porting.lcp = ''
				this.lcpIsSameAsRh = null
			}
			this.lookupLoading = false
		},
		onStepChange(newStep) {
			if (newStep === 2) {
				this.lookupRangeholder()
			}
		},
		async nextStep() {
			if (this.currentStep === 1) {
				this.lookupLoading = true
				try {
					const result = await numberportsApi.numberLookup(this.porting.number)
					if (result && result.data) {
						if (result.data.data?.lcp) this.porting.currentProvider = result.data.data.lcp
						if (result.data.data?.min_days) this.lookupMinDays = result.data.data.min_days
					}
				} catch (e) {
					console.error('Number lookup error:', e)
				}
				this.lookupLoading = false
				if (this.currentStep < this.stepsCount - 1 && !this.isStepDisabled) {
					this.currentStep++
				}
				return
			}
			if (this.currentStep < this.stepsCount - 1 && !this.isStepDisabled) {
				this.currentStep++
			}
		},
		prevStep() {
			if (this.currentStep > 0) {
				this.currentStep--
			}
		},
		addNumberRange() {
			if (!this.canAddNumberRange) return
			let start = this.singleNumberInput.trim()
			let end = this.upperRangeInput.trim()
			let numbers = []
			if (end && end !== start) {
				let startNum = parseInt(start)
				let endNum = parseInt(end)
				if (isNaN(startNum) || isNaN(endNum) || endNum < startNum) return
				for (
					let n = startNum;
					n <= endNum && numbers.length + this.porting.associated_numbers.length < 100;
					n++
				) {
					numbers.push({ number: n.toString().padStart(11, '0'), action: 'port' })
				}
			} else {
				numbers.push({ number: start.padStart(11, '0'), action: 'port' })
			}
			this.porting.associated_numbers = [...this.porting.associated_numbers, ...numbers].slice(0, 100)
			this.singleNumberInput = ''
			this.upperRangeInput = ''
		},
		removeNumber(idx) {
			this.porting.associated_numbers.splice(idx, 1)
		},
		setAllAssociatedNumbersAction(action) {
			this.porting.associated_numbers.forEach((num) => {
				num.action = action
			})
		},
		handleLcpSelection(isSame) {
			this.lcpIsSameAsRh = isSame

			if (isSame) {
				if (this.rhNotFound) {
					this.porting.currentProvider = this.porting.currentProvider + ' (Unverified)'
					this.porting.lcp = this.porting.currentProvider
				} else {
					this.porting.currentProvider = this.porting.rh
					this.porting.lcp = this.porting.rh
					this.$nextTick(() => this.$forceUpdate())
				}
			} else {
				this.porting.currentProvider = ''
				this.porting.otherProvider = ''
				this.porting.lcp = ''
				this.$nextTick(() => this.$forceUpdate())
			}
		}
	}
}
</script>
